"use client";

import Image from "next/image";
import { useMemo, useState } from "react";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

/**
 * Embla-backed cluster image carousel.
 *
 * Split into its own file so the parent can dynamic-import with
 * `{ ssr: false }` — `useEmblaCarousel` mutates the DOM on first render
 * and triggers Next's hydration-mismatch detector otherwise.
 */
export function ClusterCarousel({ images, fallbackImage }: { images: string[]; fallbackImage: string }) {
  const [fallbackIndexes, setFallbackIndexes] = useState<Set<number>>(new Set());
  const [hiddenIndexes, setHiddenIndexes] = useState<Set<number>>(new Set());

  const visibleSlides = useMemo(
    () => images.map((imageUrl, index) => ({ imageUrl, index })).filter((item) => !hiddenIndexes.has(item.index)),
    [images, hiddenIndexes],
  );

  if (images.length === 0) return null;
  if (visibleSlides.length === 0) return null;

  const markFallback = (index: number) => {
    setFallbackIndexes((current) => {
      const next = new Set(current);
      next.add(index);
      return next;
    });
  };

  const hideSlide = (index: number) => {
    setHiddenIndexes((current) => {
      const next = new Set(current);
      next.add(index);
      return next;
    });
  };

  return (
    <Carousel>
      <CarouselContent>
        {visibleSlides.map(({ imageUrl, index }) => {
          const src = fallbackIndexes.has(index) ? fallbackImage : imageUrl;
          return (
          <CarouselItem key={index} className="relative aspect-video w-full rounded-lg">
            <Image
              src={src}
              alt=""
              fill
              className="object-cover"

              onError={() => {
                if (src === fallbackImage) {
                  hideSlide(index);
                  return;
                }
                markFallback(index);
              }}
            />
          </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 z-10" />
      <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 z-10" />
    </Carousel>
  );
}
